import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";
import { useTranslation } from "react-i18next";
import { formatChatTimestamp } from "../../../shared/chat/chatActiveChats"; 
import api from "../../../shared/api/axios"; // assuming you have an API setup
import { useParams } from "react-router-dom";
import { useChat } from "../../../shared/contexts/ChatContext";
import { useActiveChats } from "../../../shared/contexts/ActiveChatsContext";

export type ChatUser = {
  _id: string;
  profilename: string;
  lastMessage?: string;
  picture?: string;
  unread?: number;
  lastMessageType?: "text" | "image" | "video" | "audio" | "media";
  lastMessageAt?: string | number | Date;
};

type ActiveChatMobileProps = {
  search: string;
  onSearchChange: (value: string) => void;
  suggestions: ChatUser[];
  onSelectSuggestion: (u: ChatUser) => void;
  activeChats: ChatUser[];
  selectedUserId?: string | null;
  onSelectChat: (u: ChatUser) => void;
  onDeleteChat: (chatUserId: string) => void;
};

type MediaLabels = {
  image: string;
  video: string;
  audio: string;
  media: string;
};

const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';

const toAbsUrl = (p?: string): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${String(p).replace(/^\/+/, '')}`;
};

const getPreview = (
  user: Pick<ChatUser, "lastMessage" | "lastMessageType">,
  labels: MediaLabels,
  max = 70
) => {
  const text = user.lastMessage?.replace(/\s+/g, " ").trim();

  // ✅ Prefer actual message text if present
  if (text) {
    return text.slice(0, max);
  }

  // ⬇️ Fallback ONLY if no text exists
  const t = user.lastMessageType || "text";
  if (t === "image") return labels.image;
  if (t === "video") return labels.video;
  if (t === "audio") return labels.audio;

  return labels.media;
};



function Avatar({
  picture,
  name,
  size = 55,
}: {
  picture?: string;
  name?: string;
  size?: number;
}) {
  const [imgOk, setImgOk] = React.useState(true);
  const src = toAbsUrl(picture);
  const showImg = !!src && imgOk;

  // reset error state when picture changes
  React.useEffect(() => {
    setImgOk(true);
  }, [src]);

  
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        background: showImg ? "transparent" : "rgb(38, 29, 162)",
        overflow: "hidden",
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 800,
        fontSize: 22,
      }}
      aria-label={name || "User"}
      title={name || "User"}
    >
      {showImg ? (
        <img
          src={src}
          alt={name || "User"}
          width={size}
          height={size}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={() => setImgOk(false)}
        />
      ) : (
        "👻"
      )}
    </div>
  );
}

const ActiveChatMobile: React.FC<ActiveChatMobileProps> = ({
  search,
  onSearchChange,
  suggestions,
  onSelectSuggestion,
  selectedUserId,
  onSelectChat,
  onDeleteChat,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { userId } = useParams();
  const { setActiveChatUserId } = useChat();


  const [menuOpenId, setMenuOpenId] = React.useState<string | null>(null);
  const [hiddenChatIds, setHiddenChatIds] = React.useState<Set<string>>(new Set());
 
const { activeChats, markChatAsRead } = useActiveChats();


  const [searchLocal, setSearchLocal] = useState("");
const [searchResults, setSearchResults] = useState<ChatUser[]>([]);
const [searchOpen, setSearchOpen] = useState(false);


  const visibleActiveChats = activeChats.filter((c) => !hiddenChatIds.has(c._id));

  useEffect(() => {
  if (userId) setActiveChatUserId(userId);
}, [userId]);




  const unhideChat = React.useCallback((id: string) => {
    setHiddenChatIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const mediaLabels: MediaLabels = {
    image: t("activeChatMobile.preview.image"),
    video: t("activeChatMobile.preview.video"),
    audio: t("activeChatMobile.preview.audio"),
    media: t("activeChatMobile.preview.media"),
  };

 

  useEffect(() => {
  if (!searchLocal.trim()) {
    setSearchResults([]);
    return;
  }

  const run = async () => {
    try {
      const res = await api.get("/users/search", {
        params: { query: searchLocal, limit: 20 }
      });
      setSearchResults(res.data.items || res.data || []);
    } catch {
      setSearchResults([]);
    }
  };

  const tmr = setTimeout(run, 300);
  return () => clearTimeout(tmr);
}, [searchLocal]);

useEffect(() => {
  console.log("✅ ActiveChatMobile preview data", activeChats);
}, [activeChats]);

useEffect(() => {
  if (userId) {
    setActiveChatUserId(userId);
    markChatAsRead(userId);
  }
}, [userId]);

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.stickyHeader}>
        <div style={styles.headerBar}>
          <button
  type="button"
  style={styles.logoBtn}
  onClick={() => navigate("/gallery")}
  aria-label="Go to gallery"
>
  <HighLogo
    src={logo}
    style={styles.logo}
    padding={0}
  />
</button>
        </div>
        <div style={styles.searchWrap}>
 <input
  value={searchLocal}
  onFocus={() => setSearchOpen(true)}
  onChange={(e) => setSearchLocal(e.target.value)}
  placeholder={t("sidebarChat.search.placeholder")}
  style={styles.searchInput}
/>

{Boolean(searchLocal.trim()) && searchResults.length > 0 && (
            <div style={styles.suggestionsPanel}>
              {searchResults.map((u) => (
                <div
                  key={u._id}
                  style={styles.suggestionItem}
                  onClick={() => {
                    unhideChat(u._id);
                    onSelectSuggestion(u);
                  }}
                >
                  <Avatar picture={u.picture} name={u.profilename} size={36} />
                  <div style={styles.suggestionTextWrap}>
                    <div style={styles.suggestionName}>{u.profilename}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

{/* Chat List */}
<div style={styles.chatList}>
  <h3 style={styles.listHeader}>
    {t("sidebarChat.list.activeTitle")}
  </h3>

  {visibleActiveChats.map((chatUser) => {
    const tsLabel = formatChatTimestamp(chatUser.lastMessageAt);
    const active = selectedUserId === chatUser._id;
    const open = menuOpenId === chatUser._id;

    return (
      <div
        key={chatUser._id}
        style={styles.chatListItem(active)}
        onClick={() => {
  setMenuOpenId(null);
  unhideChat(chatUser._id);

  markChatAsRead(chatUser._id); // ✅ THIS clears the blue badge

  setActiveChatUserId(chatUser._id);

  navigate(`/chat/${chatUser._id}`, {
    replace: false,
    state: {
      toUserId: chatUser._id,
      toUserName: chatUser.profilename,
      toUserPicture: chatUser.picture,
    },
  });
}}
        title={chatUser.lastMessage || ""}
      >
        <div style={styles.itemMain}>
          <Avatar
            picture={chatUser.picture}
            name={chatUser.profilename}
            size={44}
          />
          <div style={styles.itemLeft}>
            <span style={styles.contactName}>
              {chatUser.profilename}
            </span>
            <span style={styles.lastMessage(active)}>
              {getPreview(chatUser, mediaLabels)}
            </span>
          </div>
        </div>

        {typeof chatUser.unread === "number" && chatUser.unread > 0 && (
          <span style={styles.unreadBadge}>
            {chatUser.unread > 99 ? "99+" : chatUser.unread}
          </span>
        )}

        <div style={styles.timeStampWrap}>
          <span style={styles.timeStamp}>{tsLabel}</span>
        </div>

        {open && (
          <div style={styles.itemMenu}>
            <button
              type="button"
              style={styles.itemMenuItem}
              onClick={() => {
                setHiddenChatIds((prev) => {
                  const next = new Set(prev);
                  next.add(chatUser._id);
                  return next;
                });
                setMenuOpenId(null);
              }}
            >
              {t("activeChatMobile.menu.hideChat")}
            </button>
            <button
              type="button"
              style={styles.itemMenuItemDanger}
              onClick={() => {
                onDeleteChat(chatUser._id);
                setMenuOpenId(null);
              }}
            >
              {t("activeChatMobile.menu.deleteChat")}
            </button>
          </div>
        )}
      </div>
    );
  })}
</div>


      {searchOpen && (
  <div style={styles.searchOverlay}>
    {/* Header */}
    <div style={styles.searchOverlayHeader}>
      <input
        autoFocus
        value={searchLocal}
        onChange={(e) => setSearchLocal(e.target.value)}
        placeholder={t("sidebarChat.search.placeholder")}
        style={styles.searchOverlayInput}
      />
      <button
        onClick={() => {
          setSearchOpen(false);
          setSearchLocal("");
        }}
        style={styles.searchCloseBtn}
      >
        ✕
      </button>
    </div>

    {/* Results */}
    <div style={styles.searchOverlayResults}>
      {searchResults.length === 0 && (
        <div style={{ padding: 20, color: "#9aa0a6" }}>
          {t("sidebarChat.search.noResults")}
        </div>
      )}

      {searchResults.map((u) => (
        <div
          key={u._id}
          style={styles.suggestionItem}
          onClick={() => {
            unhideChat(u._id);
            onSelectSuggestion(u);
            setSearchOpen(false);
            setSearchLocal("");
          }}
        >
          <Avatar picture={u.picture} name={u.profilename} size={44} />
          <div style={styles.suggestionTextWrap}>
            <div style={styles.suggestionName}>{u.profilename}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

    </div>
  );
};

export default ActiveChatMobile;


const styles = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  } as React.CSSProperties,

  stickyHeader: {
    padding: "14px 2px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    background: "inherit",
  } as React.CSSProperties,

  headerBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
  } as React.CSSProperties,

  logoBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
  } as React.CSSProperties,

  logo: {
    width: 120,
    height: "auto",
    marginLeft: '-20px',
  } as React.CSSProperties,

  searchWrap: {
  position: "relative",
  width: "100%",          // ✅ allow full width
} as React.CSSProperties,


  searchInput: {
    width: "100%",
    padding: "12px 10px",
    borderRadius: 10,
    border: "1px solid #333",
    outline: "none",
    background: "#111",
    color: "#ddd",
    boxSizing: "border-box",
    fontSize: 18,
  } as React.CSSProperties,

  suggestionsPanel: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    background: "#111",
    border: "1px solid #2a2a2a",
    borderRadius: 12,
    padding: 8,
    maxHeight: 420,
    overflowY: "auto",
  } as React.CSSProperties,

  suggestionItem: {
    padding: "10px 10px",
    cursor: "pointer",
    background: "#111",
    borderRadius: 12,
    border: "1px solid #2a2a2a",
    marginBottom: 8,
    color: "#ddd",
    display: "flex",
    alignItems: "center",
    gap: 10,
  } as React.CSSProperties,

  chatList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: 4,
  } as React.CSSProperties,

  chatListItem: (active: boolean) =>
    ({
      position: "relative",
      padding: "8px 12px",
      cursor: "pointer",
      background: active ? "#5b21b6" : "#111",
      color: active ? "#fff" : "#ddd",
      borderRadius: 12,
      border: active ? "1px solid #4c1d95" : "1px solid #2a2a2a",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 6,
      marginBottom: 8,
      minHeight: 64,
    } as React.CSSProperties),

  itemMain: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  } as React.CSSProperties,

  itemLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  } as React.CSSProperties,

  itemRight: {
    position: "absolute",
    top: 8,
    right: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as React.CSSProperties,

  contactName: {
    fontWeight: 700,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  } as React.CSSProperties,

  lastMessage: (active: boolean) =>
    ({
      fontSize: "0.85em",
      marginTop: 3,
      color: active ? "rgba(255,255,255,.85)" : "#9aa0a6",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
    } as React.CSSProperties),

  unreadBadge: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 9999,
    background: "#1d93e1",
    color: "#fff",
    fontSize: 12,
    lineHeight: "20px",
    textAlign: "center",
    fontWeight: 800,
  } as React.CSSProperties,

  timeStampWrap: {
    position: "absolute",
    right: 12,
    bottom: 3,
  } as React.CSSProperties,

  timeStamp: {
    fontSize: 10,
    opacity: 0.75,
    lineHeight: "12px",
    whiteSpace: "nowrap",
  } as React.CSSProperties,

  itemMenu: {
    position: "absolute",
    top: -2,
    right: 40,
    background: "#1b1b1b",
    color: "#ddd",
    border: "1px solid #2a2a2a",
    borderRadius: 10,
    padding: 6,
    minWidth: 160,
  } as React.CSSProperties,

  itemMenuItem: {
    display: "block",
    padding: "5px 7px",
    background: "transparent",
    border: "none",
    color: "#ddd",
    cursor: "pointer",
    borderRadius: 8,
  } as React.CSSProperties,

  itemMenuItemDanger: {
    display: "block",
    padding: "5px 7px",
    background: "transparent",
    border: "none",
    color: "#ff6b6b",
    cursor: "pointer",
    borderRadius: 8,
  } as React.CSSProperties,

  suggestionTextWrap: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    } as React.CSSProperties,
    

    suggestionName: {
        fontWeight: 700,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      } as React.CSSProperties,
      
      listHeader: {
          marginTop: 8,
          marginBottom: 10,
          color: "#9aa0a6",
          fontWeight: 600,
          letterSpacing: 0.2,
          fontSize: 14,
        } as React.CSSProperties,

        searchOverlay: {
  position: "fixed",
  inset: 0,
  background: "#0b0b0b",
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
} as React.CSSProperties,

searchOverlayHeader: {
  display: "flex",
  gap: 10,
  padding: "12px",
  borderBottom: "1px solid #222",
} as React.CSSProperties,

searchOverlayInput: {
  flex: 1,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid #333",
  outline: "none",
  background: "#111",
  color: "#ddd",
  fontSize: 18,
} as React.CSSProperties,

searchCloseBtn: {
  border: "none",
  background: "transparent",
  color: "#fff",
  fontSize: 22,
  padding: "0 10px",
  cursor: "pointer",
} as React.CSSProperties,

searchOverlayResults: {
  flex: 1,
  overflowY: "auto",
  padding: 12,
} as React.CSSProperties,

      
};
